import React from 'react';
import { Link } from 'react-router-dom';
import { HiArrowNarrowRight, HiOutlineExternalLink } from 'react-icons/hi';
import { MessageComposition, Routing, Template } from '../../../../types/communication';
import { ChannelTile } from '../composition/shared/channelVisuals';

interface RoutingMappingTableProps {
    routing: Routing;
    metas: MessageComposition[];
    templates: Template[];
}

const RoutingMappingTable: React.FC<RoutingMappingTableProps> = ({ routing, metas, templates }) => (
    <div className="flex flex-col gap-4">
        {routing.targets.map((target, index) => {
            const meta = metas.find((m) => m.id === target.messageCompositionId);
            const metaTemplates = meta?.templates?.length
                ? meta.templates
                : templates.filter((t) => meta?.templateIds.includes(t.id));
            const mappingToField = (variableName: string): string =>
                variableName === 'amount'
                    ? 'amountFormatted'
                    : variableName === 'dueDate' || variableName === 'effectiveDueDate'
                      ? 'dueDateFormatted'
                      : variableName;
            const labels = new Map(
                metaTemplates.flatMap((t) =>
                    t.variables.map((v) => [v.field ?? v.name, v.label] as const),
                ),
            );
            return (
                <section key={index} className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <header className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-slate-700">
                        <ChannelTile channel={target.channel} />
                        <div className="flex-1 min-w-0">
                            {meta ? (
                                <Link
                                    to={`/communication/message-composition/${meta.id}`}
                                    className="group inline-flex items-center gap-1 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:text-teal-700 hover:underline max-w-full"
                                    title="Abrir a composição"
                                >
                                    <span className="truncate">{meta.description || meta.name}</span>
                                    <HiOutlineExternalLink size={13} className="shrink-0 opacity-50 group-hover:opacity-100" />
                                </Link>
                            ) : (
                                <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">Composição não encontrada</div>
                            )}
                            <div className="text-[11px] text-gray-400">
                                {target.groupName ? `Grupo ${target.groupName} · ` : ''}
                                {target.sendMode === 'SCHEDULED' ? `agendado ${target.scheduledAt ?? ''}` : 'envio imediato'} · até {target.limits.maxItems}{' '}
                                itens ({target.limits.truncateStrategy})
                            </div>
                        </div>
                    </header>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                                <th className="px-4 py-2 font-medium">Campo do evento</th>
                                <th className="w-8" />
                                <th className="px-4 py-2 font-medium">Preenche</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                            {target.fieldMappings.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="px-4 py-3 text-xs text-gray-500">
                                        Esta regra não declara mapeamento de variáveis — o preenchimento é feito pelo
                                        serviço (processamento dedicado), não por mapeamento campo a campo.
                                    </td>
                                </tr>
                            )}
                            {target.fieldMappings.map((mapping) => (
                                <tr key={mapping.variableName}>
                                    <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-200">{mapping.eventField}</td>
                                    <td className="text-gray-300">
                                        <HiArrowNarrowRight size={16} />
                                    </td>
                                    <td className="px-4 py-2">
                                        <span className="text-gray-800 dark:text-gray-100">{labels.get(mappingToField(mapping.variableName)) ?? mapping.variableName}</span>
                                        <span className="ml-2 font-mono text-[11px] text-gray-400">{mapping.variableName}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            );
        })}
    </div>
);

export default RoutingMappingTable;
